import type { MaBrandDetail } from '../../services/microbrandAtlas.types';

/**
 * Durable cache policy for External Brand Proposals — pure, so it is tested
 * without Firestore. Storage: services/externalProposalCache.ts.
 *
 * Why it exists: proposals are read live from Microbrand Atlas; if MA goes
 * offline (or stops publishing a brand) the fetched data would be gone. Each
 * opened proposal's full MA payload is kept in Firestore, served from there
 * while fresh, re-fetched once older than {@link PROPOSAL_CACHE_MAX_AGE_DAYS},
 * and kept as a fallback whenever a re-fetch fails.
 */

/** No existing admin convention for this; 7 days is the agreed default. */
export const PROPOSAL_CACHE_MAX_AGE_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

export type PlatformVerdict = 'shopify' | 'woocommerce' | 'neither' | 'unreachable';

export interface PlatformCheck {
  verdict: PlatformVerdict;
  checkedAt: Date;
  /** The site root the probe ran against. */
  siteUrl: string;
}

/** One cached proposal, as the app sees it. */
export interface ProposalCacheRecord {
  slug: string;
  maId: string;
  detail: MaBrandDetail;
  fetchedAt: Date;
  platformCheck?: PlatformCheck | null;
}

export function isProposalCacheStale(fetchedAt: Date, now: Date = new Date()): boolean {
  return now.getTime() - fetchedAt.getTime() > PROPOSAL_CACHE_MAX_AGE_DAYS * DAY_MS;
}

/** Whole days since `date`, for "Cached N days ago". */
export function daysAgo(date: Date, now: Date = new Date()): number {
  return Math.max(0, Math.floor((now.getTime() - date.getTime()) / DAY_MS));
}

export function agoLabel(date: Date, now: Date = new Date()): string {
  const days = daysAgo(date, now);
  if (days === 0) return 'today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

/**
 * The MA payload is stored as a JSON string, not a Firestore map: MA rows can
 * hold shapes Firestore rejects (nested arrays) and keys we never asked for,
 * and a string round-trips the payload exactly as MA served it.
 */
export function serializeDetail(detail: MaBrandDetail): string {
  return JSON.stringify(detail);
}

export function parseDetail(json: unknown): MaBrandDetail | null {
  if (typeof json !== 'string') return null;
  try {
    const value = JSON.parse(json) as MaBrandDetail;
    return value && typeof value === 'object' && typeof value.slug === 'string' ? value : null;
  } catch {
    return null;
  }
}

/** Where the detail on screen came from. */
export type DetailSource = 'live' | 'cache' | 'stale';

export interface LoadedDetail {
  /** `null` = MA has no published brand at this slug and nothing is cached. */
  detail: MaBrandDetail | null;
  source: DetailSource;
  fetchedAt: Date | null;
  platformCheck: PlatformCheck | null;
  /** Why the live fetch was not used when serving a stale copy. */
  liveProblem: string | null;
}

export interface ProposalCacheStore {
  read(slug: string): Promise<ProposalCacheRecord | null>;
  write(record: ProposalCacheRecord): Promise<void>;
}

/**
 * Decide and perform one load:
 *  - fresh cache (and not forced) → the cache, no MA request;
 *  - otherwise MA live → write it back to the cache (best effort);
 *  - MA fails, or no longer publishes the brand → the cached copy, marked
 *    stale with the reason, if there is one; else the failure / null as before.
 * A cache that cannot be read or written (rules not deployed, offline) never
 * breaks the load — the tool behaves as it did before the cache existed.
 */
export async function loadProposalDetail(
  slug: string,
  deps: {
    fetchLive: (slug: string) => Promise<MaBrandDetail | null>;
    store: ProposalCacheStore;
    now?: () => Date;
    onCacheError?: (error: unknown) => void;
  },
  options: { force?: boolean } = {},
): Promise<LoadedDetail> {
  const now = deps.now ?? (() => new Date());
  let cached: ProposalCacheRecord | null = null;
  try {
    cached = await deps.store.read(slug);
  } catch (error) {
    deps.onCacheError?.(error);
  }

  if (cached && !options.force && !isProposalCacheStale(cached.fetchedAt, now())) {
    return {
      detail: cached.detail,
      source: 'cache',
      fetchedAt: cached.fetchedAt,
      platformCheck: cached.platformCheck ?? null,
      liveProblem: null,
    };
  }

  let live: MaBrandDetail | null;
  try {
    live = await deps.fetchLive(slug);
  } catch (error) {
    if (!cached) throw error;
    return {
      detail: cached.detail,
      source: 'stale',
      fetchedAt: cached.fetchedAt,
      platformCheck: cached.platformCheck ?? null,
      liveProblem: `Microbrand Atlas could not be reached (${error instanceof Error ? error.message : String(error)})`,
    };
  }

  if (!live) {
    if (!cached) return { detail: null, source: 'live', fetchedAt: null, platformCheck: null, liveProblem: null };
    return {
      detail: cached.detail,
      source: 'stale',
      fetchedAt: cached.fetchedAt,
      platformCheck: cached.platformCheck ?? null,
      liveProblem: 'Microbrand Atlas no longer publishes this brand',
    };
  }

  const fetchedAt = now();
  try {
    await deps.store.write({
      slug,
      maId: live.id,
      detail: live,
      fetchedAt,
      platformCheck: cached?.platformCheck ?? null,
    });
  } catch (error) {
    deps.onCacheError?.(error);
  }
  return {
    detail: live,
    source: 'live',
    fetchedAt,
    platformCheck: cached?.platformCheck ?? null,
    liveProblem: null,
  };
}
